import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { Video as LucideIcon } from 'lucide-react';
import { getTaskDisplay } from '../shared/taskTypeConfig';
import { getRoleFilterInfo } from '../utils/roleFilters';

interface RawTask {
  task_id: string;
  lead_id: string | null;
  agent: string | null;
  task_type: string | null;
  status: string;
  run_at: string | null;
  created_at: string;
  updated_at: string | null;
  executed_at: string | null;
  client_id: string | null;
  user_id: string | null;
  json: Record<string, unknown> | null;
}

export interface TaskFeedItem {
  taskId: string;
  leadName: string;
  company: string;
  timestamp: Date;
  status: 'processing' | 'finished';
  description: string;
  icon: LucideIcon;
  taskType: string;
}

export interface PendingTaskItem {
  taskId: string;
  leadName: string;
  company: string;
  scheduledTime: Date;
  description: string;
  icon: LucideIcon;
  taskType: string;
}

interface LeadInfo {
  lead_id: string;
  dm_full_name: string | null;
  company_name: string | null;
}

const EXCLUDED_AGENTS = ['testemailagent', 'testwhatsappagent', 'testlinkedinagent', 'analyzer', 'meetingbooker'];

function extractLeadInfo(task: RawTask): { name: string; company: string } {
  const json = task.json as Record<string, unknown> | null;
  const name = (json?.dm_full_name as string) || (json?.contact_name as string) || (json?.industry as string) || '';
  const company = (json?.company_name as string) || (json?.country as string) || '';
  return { name, company };
}

export function useTasksQueue(agentFilter: string | string[]) {
  const [processingTasks, setProcessingTasks] = useState<TaskFeedItem[]>([]);
  const [recentFinishedTasks, setRecentFinishedTasks] = useState<TaskFeedItem[]>([]);
  const [pendingTasks, setPendingTasks] = useState<PendingTaskItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const isMountedRef = useRef(true);
  const leadCacheRef = useRef<Map<string, LeadInfo>>(new Map());

  const agents = useMemo(() => {
    const arr = Array.isArray(agentFilter) ? agentFilter : [agentFilter];
    return arr.filter(a => !EXCLUDED_AGENTS.includes(a));
  }, [JSON.stringify(agentFilter)]);

  const resolveLeadNames = useCallback(async (tasks: RawTask[]): Promise<Map<string, LeadInfo>> => {
    const cache = leadCacheRef.current;
    const unknownIds = new Set<string>();

    for (const task of tasks) {
      if (task.lead_id && !cache.has(task.lead_id)) {
        unknownIds.add(task.lead_id);
      }
    }

    if (unknownIds.size === 0) return cache;

    const idsArray = Array.from(unknownIds);

    const { data: trackingData } = await supabase
      .from('leads_tracking')
      .select('lead_id, dm_full_name, company_name')
      .in('lead_id', idsArray);

    if (trackingData) {
      for (const row of trackingData) {
        cache.set(row.lead_id, row);
      }
    }

    const stillMissing = idsArray.filter(id => !cache.has(id));
    if (stillMissing.length > 0) {
      const { data: pqlData } = await supabase
        .from('pre_qualified_leads')
        .select('lead_id, dm_full_name, company_name')
        .in('lead_id', stillMissing);

      if (pqlData) {
        for (const row of pqlData) {
          if (row.lead_id) {
            cache.set(row.lead_id, { lead_id: row.lead_id, dm_full_name: row.dm_full_name, company_name: row.company_name });
          }
        }
      }
    }

    return cache;
  }, []);

  const buildFeedItem = useCallback((task: RawTask, cache: Map<string, LeadInfo>): TaskFeedItem => {
    const display = getTaskDisplay(task.task_type || '');
    let leadName = '';
    let company = '';

    if (task.lead_id && cache.has(task.lead_id)) {
      const info = cache.get(task.lead_id)!;
      leadName = info.dm_full_name || '';
      company = info.company_name || '';
    }

    if (!leadName || !company) {
      const fallback = extractLeadInfo(task);
      if (!leadName) leadName = fallback.name;
      if (!company) company = fallback.company;
    }

    if (!leadName && company) {
      leadName = company;
      company = '';
    }

    return {
      taskId: task.task_id,
      leadName: leadName || 'Unknown lead',
      company,
      timestamp: new Date(task.updated_at || task.created_at),
      status: task.status === 'processing' ? 'processing' : 'finished',
      description: display.label,
      icon: display.icon,
      taskType: task.task_type || '',
    };
  }, []);

  const buildPendingItem = useCallback((task: RawTask, cache: Map<string, LeadInfo>): PendingTaskItem => {
    const display = getTaskDisplay(task.task_type || '');
    let leadName = '';
    let company = '';

    if (task.lead_id && cache.has(task.lead_id)) {
      const info = cache.get(task.lead_id)!;
      leadName = info.dm_full_name || '';
      company = info.company_name || '';
    }

    if (!leadName || !company) {
      const fallback = extractLeadInfo(task);
      if (!leadName) leadName = fallback.name;
      if (!company) company = fallback.company;
    }

    if (!leadName && company) {
      leadName = company;
      company = '';
    }

    return {
      taskId: task.task_id,
      leadName: leadName || 'Unknown lead',
      company,
      scheduledTime: new Date(task.run_at || task.created_at),
      description: display.label,
      icon: display.icon,
      taskType: task.task_type || '',
    };
  }, []);

  const fetchData = useCallback(async (isBackground = false) => {
    if (agents.length === 0) return;

    const roleInfo = getRoleFilterInfo();
    if (!roleInfo) return;

    try {
      if (!isBackground) setIsLoading(true);

      const cols = 'task_id, lead_id, agent, task_type, status, run_at, created_at, updated_at, executed_at, client_id, user_id, json';

      const applyRoleFilter = (query: any) => {
        let q = query.eq('client_id', roleInfo.clientId);
        if (!roleInfo.isAdmin) q = q.eq('user_id', roleInfo.userId);
        return q;
      };

      const [processingRes, finishedRes, pendingRes] = await Promise.all([
        applyRoleFilter(
          supabase
            .from('tasks_queue')
            .select(cols)
            .in('agent', agents)
            .eq('status', 'processing')
        )
          .order('updated_at', { ascending: false })
          .limit(6),

        applyRoleFilter(
          supabase
            .from('tasks_queue')
            .select(cols)
            .in('agent', agents)
            .eq('status', 'finished')
        )
          .order('updated_at', { ascending: false })
          .limit(20),

        applyRoleFilter(
          supabase
            .from('tasks_queue')
            .select(cols)
            .in('agent', agents)
            .eq('status', 'pending')
        )
          .order('run_at', { ascending: true })
          .limit(20),
      ]);

      const allTasks = [
        ...(processingRes.data || []),
        ...(finishedRes.data || []),
        ...(pendingRes.data || []),
      ] as RawTask[];

      const cache = await resolveLeadNames(allTasks);

      if (!isMountedRef.current) return;

      const processingItems = (processingRes.data || []).map((t: RawTask) => buildFeedItem(t, cache));
      const finishedItems = (finishedRes.data || []).map((t: RawTask) => buildFeedItem(t, cache));
      const pendingItems = (pendingRes.data || []).map((t: RawTask) => buildPendingItem(t, cache));

      setProcessingTasks(processingItems);
      setRecentFinishedTasks(finishedItems.slice(0, 6));
      setPendingTasks(pendingItems.slice(0, 6));
    } catch (err) {
      console.error('[useTasksQueue] Error fetching tasks:', err);
    } finally {
      if (isMountedRef.current) setIsLoading(false);
    }
  }, [agents, resolveLeadNames, buildFeedItem, buildPendingItem]);

  const fetchDataRef = useRef(fetchData);
  fetchDataRef.current = fetchData;

  useEffect(() => {
    isMountedRef.current = true;
    fetchDataRef.current(false);

    const channel = supabase
      .channel(`tasks_queue_${agents.join('_')}_${Math.random()}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tasks_queue',
        },
        () => {
          fetchDataRef.current(true);
        }
      )
      .subscribe();

    return () => {
      isMountedRef.current = false;
      supabase.removeChannel(channel);
    };
  }, [agents.join(',')]);

  const hasProcessing = processingTasks.length > 0;
  const hasAnyTasks = processingTasks.length > 0 || recentFinishedTasks.length > 0 || pendingTasks.length > 0;

  return {
    processingTasks,
    recentFinishedTasks,
    pendingTasks,
    isLoading,
    hasProcessing,
    hasAnyTasks,
  };
}
